<template>
  <ValidationObserver ref="form" v-slot="{ handleSubmit, invalid }" slim>
    <form class="auth-form" @submit.prevent="handleSubmit(handleSignUp)">
      <!--switch login-->
      <div v-if="layout === 'B' && menu === 'button'" class="mb-4 text-center">
        <span class="me-2">{{ $t('component.SignUpModal.login') }}</span>
        <a
          href="#"
          class="text-decoration-underline auth-form-link"
          @click.prevent="$emit('switchCategory', 'login')"
          >{{ $t('component.AuthModal.list.login') }}</a
        >
      </div>

      <!-- 使用者名稱 -->
      <div class="mb-4">
        <label for="regUsername" class="form-label required">
          {{ $t('common.profile.username') }}
        </label>
        <ValidationProvider
          ref="regUsernameRef"
          v-slot="{ valid, invalid, dirty }"
          vid="regUsername"
          :name="$t('common.profile.username')"
          :rules="customUsernameRule"
          slim
        >
          <input
            id="regUsername"
            v-model="regUsername"
            :class="{
              'is-valid': valid,
              'is-invalid': dirty && invalid
            }"
            name="regUsername"
            type="text"
            class="form-control"
            autofocus
            :placeholder="$t('component.SignUpModal.regUsername.placeholder')"
          />
        </ValidationProvider>
      </div>
      <!-- 真實姓名  -->
      <div class="mb-4">
        <label for="regActualName" class="form-label required">
          {{ $t('common.profile.realName') }}
        </label>
        <ValidationProvider
          ref="regActualNameRef"
          v-slot="{ valid, invalid, dirty }"
          vid="regActualName"
          :name="$t('common.profile.realName')"
          :rules="{
            required: true,
            regex: new RegExp(/^[\u4E00-\u9FFF\u30A0-\u30FF\u3005]+$/)
          }"
          slim
        >
          <input
            id="regActualName"
            v-model="regActualName"
            :class="{
              'is-valid': valid,
              'is-invalid': dirty && invalid
            }"
            name="regActualName"
            type="text"
            class="form-control"
            autofocus
            :placeholder="$t('component.SignUpModal.regActualName.placeholder')"
          />
        </ValidationProvider>
      </div>
      <!-- email -->
      <div class="mb-4">
        <label class="form-label required">
          {{ $t('common.profile.email') }}
        </label>
        <div class="row gx-3 align-items-center">
          <div class="col">
            <ValidationProvider
              ref="regEmailRef"
              v-slot="{ valid, invalid, dirty }"
              vid="regEmail"
              :name="$t('common.profile.email')"
              rules="email|required"
              slim
            >
              <input
                v-model="regEmail"
                :class="{
                  'is-valid': valid,
                  'is-invalid': dirty && invalid
                }"
                name="regEmail"
                type="email"
                class="form-control"
                :placeholder="$t('component.SignUpModal.regEmail.placeholder')"
                @blur="checkIsOneTimeEmail"
              />
            </ValidationProvider>
          </div>
          <div class="col-auto">
            <button
              v-if="waitingToResendTime <= 0"
              type="button"
              class="btn"
              :disabled="
                !regEmail || ($refs.regEmailRef && $refs.regEmailRef.errors[0])
              "
              :class="customBtnPrimaryClass"
              @click.prevent="sendVerifyCode"
            >
              {{ $t('component.SignUpModal.regEmailVerifyCode.buttons.send') }}
            </button>
            <button
              v-else
              type="button"
              class="btn"
              :class="customBtnPrimaryClass"
              disabled
            >
              {{
                $t(
                  'component.SignUpModal.regEmailVerifyCode.buttons.waitToResend',
                  [waitingToResendTime]
                )
              }}
            </button>
          </div>
        </div>
        <small v-if="isOneTimeEmail" class="d-block invalid-feedback">
          {{ $t('validate.oneTimeEmail') }}
        </small>
      </div>
      <!-- email 認證 -->
      <div class="mb-4">
        <label for="regEmailVerifyCode" class="form-label required">
          {{ $t('component.SignUpModal.regEmailVerifyCode.label') }}
        </label>
        <ValidationProvider
          ref="regEmailVerifyCodeRef"
          v-slot="{ valid, invalid, dirty }"
          vid="regEmailVerifyCode"
          :name="$t('component.SignUpModal.regEmailVerifyCode.label')"
          rules="required"
          slim
        >
          <input
            id="regEmailVerifyCode"
            v-model="regEmailVerifyCode"
            :class="{
              'is-valid': valid,
              'is-invalid': dirty && invalid
            }"
            name="regEmailVerifyCode"
            type="text"
            class="form-control"
            :placeholder="
              $t('component.SignUpModal.regEmailVerifyCode.placeholder')
            "
          />
        </ValidationProvider>
      </div>
      <!-- 密碼 -->
      <div class="mb-4">
        <label class="form-label required">
          {{ $t('common.profile.password') }}
        </label>
        <ValidationProvider
          ref="regPasswordRef"
          v-slot="{ valid, invalid, dirty }"
          vid="regPassword"
          :name="$t('common.profile.password')"
          rules="required|min:6|max:16"
          slim
        >
          <input
            v-model="regPassword"
            :class="{
              'is-valid': valid,
              'is-invalid': dirty && invalid
            }"
            name="regPassword"
            type="password"
            class="form-control"
            :placeholder="$t('component.SignUpModal.regPassword.placeholder')"
          />
        </ValidationProvider>
      </div>
      <!-- 密碼再次驗證 -->
      <ValidationProvider
        ref="confirmPasswordRef"
        v-slot="{ valid, invalid, dirty, errors }"
        vid="confirmPassword"
        :name="$t('common.profile.password')"
        rules="required|min:6|max:16|confirmed:regPassword"
        tag="div"
        class="mb-4"
      >
        <label class="form-label required">
          {{ $t('common.profile.confirmPassword') }}
        </label>
        <input
          v-model="confirmPassword"
          name="confirmPassword"
          type="password"
          class="form-control"
          :class="{
            'is-valid': valid,
            'is-invalid': dirty && invalid
          }"
          :placeholder="$t('component.SignUpModal.confirmPassword.text')"
        />
        <div v-if="dirty && invalid" class="invalid-feedback">
          {{ errors[0] }}
        </div>
      </ValidationProvider>
      <!-- 電話 -->
      <div class="mb-4">
        <label class="form-label required">
          {{ $t('common.profile.tel') }}
        </label>
        <IntelInput
          v-model="regTel"
          :placeholder="$t('component.SignUpModal.regTel.placeholder')"
          :country-list="intelCountries"
          @validate="isValid => (isRegTelValid = isValid)"
        ></IntelInput>
      </div>
      <!-- 生日 -->
      <div class="mb-4">
        <label class="form-label required">
          {{ $t('common.profile.birthday') }}
        </label>
        <ValidationProvider
          v-if="!isMobile"
          ref="regBirthdayRef"
          v-slot="{ valid, invalid, dirty }"
          vid="regBirthday"
          :name="$t('common.profile.birthday')"
          rules="required|date"
          slim
        >
          <no-ssr>
            <date-picker
              v-model="regBirthday"
              :input-class="[
                'form-control',
                { 'is-valid': valid, 'is-invalid': dirty && invalid }
              ]"
              :placeholder="$t('component.SignUpModal.regBirthday.placeholder')"
              :lang="langForDatePicker || 'en'"
              value-type="format"
              width="100%"
              :disabled-date="
                date =>
                  $dayjs()
                    .subtract(18, 'year')
                    .isBefore($dayjs(date))
              "
              default-value="1990-01-01"
            >
              <i slot="icon-calendar" class="d-block km-icon-date"></i>
            </date-picker>
          </no-ssr>
        </ValidationProvider>
        <div v-else class="mx-datepicker position-relative">
          <ValidationProvider
            ref="regBirthdayRef"
            v-slot="{ valid, invalid, dirty }"
            vid="birthday"
            :name="$t('common.profile.birthday')"
            rules="required|date|date_after_year:18"
            slim
          >
            <input
              id="birthday"
              v-model="regBirthday"
              :class="{
                'is-valid': valid,
                'is-invalid': dirty && invalid
              }"
              name="birthday"
              type="date"
              class="form-control"
            />
          </ValidationProvider>
          <i slot="icon-calendar" class="position-absolute km-icon-date"></i>
        </div>
      </div>
      <!-- 驗證碼 -->
      <div class="mb-4">
        <label class="form-label required" for="imageCode">
          {{ $t('common.imageCode') }}
        </label>
        <div class="d-flex gap-3 align-items-center">
          <ValidationProvider
            ref="imageCodeRef"
            v-slot="{ valid, invalid, dirty }"
            vid="imageCode"
            :name="$t('common.imageCode')"
            rules="required"
            slim
          >
            <input
              id="imageCode"
              v-model="imageCode"
              :class="{
                'is-valid': valid,
                'is-invalid': dirty && invalid
              }"
              name="imageCode"
              type="text"
              class="form-control"
              autocomplete="off"
            />
          </ValidationProvider>
          <div class="verify-code-pic">
            <img
              v-if="imageSrc !== null"
              :src="'data:image/jpeg;base64, ' + imageSrc"
              alt="image-code"
            />
            <i v-if="imageSrc === null" class="km-icon-spinner" />
          </div>
          <i
            class="km-icon-arrow-rotate auth-form-link verify-code-update-icon"
            @click.prevent="refreshImageSrc"
          />
        </div>
      </div>
      <!-- 同意 -->
      <div class="mb-4">
        <div class="form-check">
          <ValidationProvider
            ref="isAgreedRef"
            v-slot="{ valid, invalid, dirty }"
            vid="isAgreed"
            name="isAgreed"
            :rules="{ required: { allowFalse: false } }"
            slim
          >
            <input
              id="isAgreed"
              v-model="isAgreed"
              type="checkbox"
              name="isAgreed"
              :class="customFormCheckInputClass"
          /></ValidationProvider>
          <label
            class="form-check-label"
            for="isAgreed"
            v-html="$t('component.SignUpModal.isAgreed.text')"
          >
          </label>
        </div>
      </div>
      <div class="mb-4">
        <div class="form-check">
          <ValidationProvider
            ref="isMarketedRef"
            v-slot="{ valid, invalid, dirty }"
            vid="isMarketed"
            name="isMarketed"
            :rules="{ required: { allowFalse: false } }"
            slim
          >
            <input
              id="isMarketed"
              v-model="isMarketed"
              type="checkbox"
              name="isMarketed"
              :class="customFormCheckInputClass"
            />
          </ValidationProvider>
          <label
            class="form-check-label"
            for="isMarketed"
            v-html="$t('component.SignUpModal.isMarketed.text')"
          >
          </label>
        </div>
      </div>
      <!-- 送出 -->
      <div class="mb-4">
        <button
          :disabled="invalid || isOneTimeEmail || !isRegTelValid"
          type="submit"
          class="w-100 btn rounded-pill"
          :class="customBtnPrimaryClass"
        >
          {{ $t('component.SignUpModal.actions.submit') }}
        </button>
      </div>

      <!--switch login-->
      <div v-if="layout === 'A' && menu === 'button'" class="text-center">
        <span class="me-2">{{ $t('component.SignUpModal.login') }}</span>
        <a
          href="#"
          class="text-decoration-underline auth-form-link"
          @click.prevent="$emit('switchCategory', 'login')"
          >{{ $t('component.AuthModal.list.login') }}</a
        >
      </div>

      <!--oauth-->
      <div
        v-if="hasTelegramOauth || hasGoogleOauth || hasLineOauth"
        class="divider my-4"
      >
        or
      </div>
      <div v-if="hasTelegramOauth || hasGoogleOauth" class="d-grid gap-3">
        <button
          v-if="hasTelegramOauth"
          class="btn btn-outline-gray btn-oauth rounded-pill"
          @click.prevent="signUpWithTelegram"
        >
          <img
            :src="
              `https://ik.imagekit.io/1pzxddx3qqv/common/img/btn_oauth_telegram.png`
            "
            alt="btn-oauth-telegram"
          />
          <span v-html="$t('common.oauth.login', ['Telegram'])"></span>
        </button>
        <button
          v-if="hasGoogleOauth"
          class="btn btn-outline-gray btn-oauth rounded-pill"
          @click.prevent="signUpWithGoogle"
        >
          <img
            :src="
              `https://ik.imagekit.io/1pzxddx3qqv/common/img/btn_oauth_google.png`
            "
            alt="btn-oauth-google"
          />
          <span v-html="$t('common.oauth.login', ['Google'])"></span>
        </button>
        <button
          v-if="hasLineOauth"
          class="btn btn-outline-gray btn-oauth rounded-pill"
          @click.prevent="signUpWithLine"
        >
          <img
            :src="
              `https://ik.imagekit.io/1pzxddx3qqv/common/img/btn_oauth_line.png`
            "
            alt="btn-oauth-line"
          />
          <span v-html="$t('common.oauth.login', ['Line'])"></span>
        </button>
      </div>
    </form>
  </ValidationObserver>
</template>

<script>
import { checkAxiosData } from 'fe-common'
import {
  computed,
  defineComponent,
  onMounted,
  ref,
  useStore
} from '@nuxtjs/composition-api'
import { catchError, concatMap, finalize, tap, from, filter } from 'rxjs'
import { handleMessages } from '../modules/client/utilities'
import useLocale from '../composables/useLocale'
import useVeeValidate from '../composables/useVeeValidate'
import useErrorHandler from '../composables/useErrorHandler'
import useUser from '../composables/useUser'
import useOauth from '../composables/useOauth'
import IntelInput from './IntelInput'

export default defineComponent({
  components: {
    IntelInput
  },
  props: {
    layout: {
      type: String,
      default: 'A'
    },
    menu: {
      type: String,
      default: 'tabs'
    },
    intelCountries: {
      type: Array,
      default: () => []
    },
    customUsernameRule: {
      type: [String, Object],
      default: () => ({
        required: true,
        regex: new RegExp(/^([1-9A-Za-z][0-9A-Za-z]*)$/),
        min: 6,
        max: 15
      })
    },
    // custom class
    customFormCheckInputClass: {
      type: String,
      default: 'form-check-input'
    },
    customBtnPrimaryClass: {
      type: String,
      default: 'btn-primary'
    }
  },
  setup(props, context) {
    const store = useStore()

    const { user } = useUser(context)

    const waitingToResendTime = ref(0)
    const isAgreed = ref(false)
    const isMarketed = ref(false)
    const confirmPassword = ref(null)
    const isOneTimeEmail = ref(false)
    const isRegTelValid = ref(false)

    const isMobile = computed(() => store.state.isMobile)
    const imageSrc = computed({
      get() {
        return store.state.authModule.imageSrc
      },
      set(value) {
        store.commit('authModule/IMAGE_SRC', value)
      }
    })
    const imageCode = computed({
      get() {
        return store.state.authModule.imageCode
      },
      set(value) {
        store.commit('authModule/IMAGE_CODE', value)
      }
    })
    const imageToken = computed({
      get() {
        return store.state.authModule.imageToken
      },
      set(value) {
        store.commit('authModule/IMAGE_TOKEN', value)
      }
    })
    const regUsername = computed({
      get() {
        return store.state.authModule.regUsername
      },
      set(value) {
        store.commit('authModule/REG_USERNAME', value)
      }
    })
    const regActualName = computed({
      get() {
        return store.state.authModule.regActualName
      },
      set(value) {
        store.commit('authModule/REG_ACTUAL_NAME', value)
      }
    })
    const regTel = computed({
      get() {
        return store.state.authModule.regTel
      },
      set(value) {
        store.commit('authModule/REG_TEL', value)
      }
    })
    const regPassword = computed({
      get() {
        return store.state.authModule.regPassword
      },
      set(value) {
        store.commit('authModule/REG_PASSWORD', value)
      }
    })
    const regEmail = computed({
      get() {
        return store.state.authModule.regEmail
      },
      set(value) {
        store.commit('authModule/REG_EMAIL', value)
      }
    })
    const regConfirmPassword = computed({
      get() {
        return store.state.authModule.regConfirmPassword
      },
      set(value) {
        store.commit('authModule/REG_CONFIRM_PASSWORD', value)
      }
    })
    const regBirthday = computed({
      get() {
        return store.state.authModule.regBirthday
      },
      set(value) {
        store.commit('authModule/REG_BIRTHDAY', value)
      }
    })
    const regRecommendedCode = computed({
      get() {
        return store.state.authModule.regRecommendedCode
      },
      set(value) {
        store.commit('authModule/REG_RECOMMENDED_CODE', value)
      }
    })
    const regEmailVerifyCode = computed({
      get() {
        return store.state.authModule.regEmailVerifyCode
      },
      set(value) {
        store.commit('authModule/REG_EMAIL_VERIFY_CODE', value)
      }
    })
    const regCurrency = computed({
      get() {
        return store.state.authModule.regCurrency
      },
      set(value) {
        store.commit('authModule/REG_CURRENCY', value)
      }
    })
    const { langForDatePicker } = useLocale()

    onMounted(() => {
      refreshData()
    })

    const { reset } = useVeeValidate(context)
    const { errorHandler, errorHandler$ } = useErrorHandler(context)
    const { checkIsAdult } = useUser(context)

    function refreshData() {
      regUsername.value = null
      regActualName.value = null
      regTel.value = null
      regEmail.value = null
      regPassword.value = null
      regRecommendedCode.value = null
      regBirthday.value = null
      regEmailVerifyCode.value = null
      regCurrency.value = 'USD'
      refreshImageSrc()
      reset()
    }

    function refreshImageSrc() {
      imageSrc.value = null
      imageCode.value = null
      imageToken.value = null
      store.dispatch('authModule/getImageSrc').catch(err => errorHandler(err))
      reset('imageCodeRef')
    }

    function checkIsOneTimeEmail() {
      if (!regEmail.value) {
        isOneTimeEmail.value = false
        return
      }
      from(store.dispatch('authModule/isOneTimeEmail'))
        .pipe(
          filter(data =>
            checkAxiosData(data, {
              handleMessages: handleMessages({
                store
              })
            })
          ),
          tap(({ data }) => {
            isOneTimeEmail.value = data
          }),
          catchError(err => {
            refreshImageSrc()
            return errorHandler$(err)
          })
        )
        .subscribe()
    }

    function handleSignUp() {
      window.dataLayer &&
        window.dataLayer.push({
          event: 'registerSubmitEvent',
          username: regUsername.value
        })

      signUp(signUpSuccess)
    }

    function signUp(successCallback) {
      const directSignUp = () => {
        from(store.dispatch('startLoading'))
          .pipe(
            concatMap(() => store.dispatch('authModule/signUp')),
            filter(data =>
              checkAxiosData(data, {
                handleMessages: handleMessages({
                  store
                })
              })
            ),
            tap(({ result }) => {
              store.commit('authModule/TOKEN', result.token)
              store.commit('authModule/TOKEN_EXP', result.exp)
              store.commit('authModule/USER', result.user)

              successCallback()
            }),
            catchError(err => {
              refreshImageSrc()
              return errorHandler$(err)
            }),
            finalize(() => store.dispatch('completeLoading'))
          )
          .subscribe()
      }

      if (!checkIsAdult(regBirthday.value)) {
        return
      }

      directSignUp()
    }

    function signUpSuccess() {
      context.emit('switchCategory', null)

      window.dataLayer &&
        window.dataLayer.push({
          event: 'registerEvent',
          username: user.value.username
        })

      context.emit('signUpSuccessAfter')
    }

    function sendVerifyCode() {
      from(store.dispatch('startLoading'))
        .pipe(
          concatMap(() => from(store.dispatch('authModule/sendEmailVerify'))),
          tap(({ data }) => {
            waitingToResendTime.value = data

            const clearIntervalId = setInterval(() => {
              if (--waitingToResendTime.value === 0)
                clearInterval(clearIntervalId)
            }, 1000)
          }),
          catchError(err => errorHandler$(err)),
          finalize(() => store.dispatch('completeLoading'))
        )
        .subscribe()
    }

    const { signInWithTelegram, signInWithGoogle, signInWithLine } = useOauth(
      context
    )
    const hasTelegramOauth = computed(() => !!process.env.OAUTH_TELEGRAM_BOT_ID)
    function signUpWithTelegram() {
      signInWithTelegram(signUpSuccess, () => {
        refreshImageSrc()
      })
    }
    const hasGoogleOauth = computed(() => !!process.env.OAUTH_GOOGLE_CLIENT_ID)
    function signUpWithGoogle() {
      signInWithGoogle(signUpSuccess, () => {
        refreshImageSrc()
      })
    }
    const hasLineOauth = computed(
      () => !!process.env.OAUTH_LINE_ID && !!process.env.OAUTH_LINE_SECRET
    )
    function signUpWithLine() {
      signInWithLine(signUpSuccess, () => {
        refreshImageSrc()
      })
    }

    return {
      waitingToResendTime,
      isAgreed,
      isMarketed,
      confirmPassword,
      isOneTimeEmail,

      isRegTelValid,
      isMobile,
      imageSrc,
      imageCode,
      imageToken,
      regUsername,
      regTel,
      regPassword,
      regEmail,
      regConfirmPassword,
      regActualName,
      regBirthday,
      regRecommendedCode,
      regEmailVerifyCode,
      regCurrency,

      langForDatePicker,

      handleSignUp,
      checkIsOneTimeEmail,
      sendVerifyCode,
      refreshImageSrc,

      hasTelegramOauth,
      signUpWithTelegram,
      hasGoogleOauth,
      signUpWithGoogle,
      hasLineOauth,
      signUpWithLine
    }
  }
})
</script>
